import React from 'react';
import { Gift, Heart, Star, Calendar} from 'lucide-react';

const BirthdayPage = ({ setCurrentPage }) => {
  const ageGroups = [
    { id: 'kids', name: 'Barn (0-12 år)', icon: '👶', description: 'Leker, spill og lærerike gaver' },
    { id: 'teens', name: 'Tenåringer (13-19 år)', icon: '🧒', description: 'Trendy og personlige gaver' },
    { id: 'adults', name: 'Voksne (20-65 år)', icon: '👩‍💼', description: 'Praktiske og gjennomtenkte gaver' },
    { id: 'seniors', name: 'Seniorer (65+ år)', icon: '👴', description: 'Komfort og opplevelser' }
  ];

  const popularIdeas = [
    { category: 'Personlige gaver', icon: '💝', items: ['Fotobøker', 'Personaliserte smykker', 'Graverte gjenstander', 'Minnesaker'] },
    { category: 'Opplevelser', icon: '🎭', items: ['Konsertbilletter', 'Restaurantbesøk', 'Spa-behandling', 'Kurs og workshops'] },
    { category: 'Hobbyer & interesser', icon: '🎨', items: ['Bøker', 'Kunstutstyr', 'Sportsutstyr', 'Håndverksmaterialer'] },
    { category: 'Teknologi', icon: '📱', items: ['Smartklokker', 'Hodetelefoner', 'Kamerautstyr', 'Smart home-produkter'] }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-purple-50">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-4xl animate-bounce">🎂</div>
          <div className="absolute top-20 right-20 text-3xl animate-pulse">🎈</div>
          <div className="absolute bottom-20 left-20 text-5xl animate-bounce">🎁</div>
          <div className="absolute bottom-10 right-10 text-4xl animate-pulse">🎉</div>
        </div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">🎂</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Bursdagsgaver</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Feir livets milepæler med gaver som virkelig gjør dagen spesiell. Fra personlige oppmerksomheter 
              til store overraskelser - finn den perfekte bursdagsgaven.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-600 text-white rounded-lg hover:from-primary-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn bursdagsgaver
            </button>
          </div>
        </div>
      </section>

      {/* Age Groups */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gaver etter alder
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {ageGroups.map((group) => (
                <div key={group.id} className="bg-gradient-to-br from-primary-50 to-purple-50 rounded-lg p-6 border border-primary-100 hover:shadow-lg transition-all">
                  <div className="text-4xl mb-4 text-center">{group.icon}</div>
                  <h3 className="text-xl font-semibold text-secondary-800 mb-2 text-center">{group.name}</h3>
                  <p className="text-secondary-600 text-sm text-center">{group.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Ideas */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Populære bursdagsgave-kategorier
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {popularIdeas.map((idea, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                  <div className="flex items-center mb-4">
                    <span className="text-3xl mr-4">{idea.icon}</span>
                    <h3 className="text-xl font-semibold text-secondary-800">{idea.category}</h3>
                  </div>
                  <ul className="space-y-2">
                    {idea.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center text-secondary-600">
                        <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Birthday Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tips for perfekte bursdagsgaver
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-primary-500" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Tenk personlig</h3>
                <p className="text-secondary-600">
                  De beste gavene viser at du kjenner personen. Hva liker de? Hva trenger de? 
                  Hva har de ønsket seg lenge?
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Timing er alt</h3>
                <p className="text-secondary-600">
                  Planlegg i god tid, spesielt for spesialbestillinger. Noen gaver blir bedre 
                  med litt ekstra forberedelse.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-indigo-500" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Opplevelser varer</h3>
                <p className="text-secondary-600">
                  Vurder opplevelser fremfor ting. Minner fra en fin opplevelse varer 
                  ofte lenger enn fysiske gjenstander.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte bursdagsgaven?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Bruk vår gavefinner for personlige anbefalinger basert på alder, interesser og budsjett
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-primary-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('occasion')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-primary-600 transition-colors font-semibold"
              >
                Se andre anledninger
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BirthdayPage;